/**
 * usePageTitle — per-page <title>, optional meta description and the
 * canonical link.
 *
 * Browser tabs, bookmarks and crawlers that run JavaScript (Google does)
 * see the page-specific title; the static defaults in index.html stay
 * for everything else. The brand name from BrandProvider is the suffix,
 * so a rebrand reaches every tab without touching the pages.
 *
 * Leaving the page restores the bare site name and drops the canonical
 * link, so a page that does not call the hook never shows the previous
 * page's title or canonical after a client-side navigation.
 */
import { useEffect } from 'react';
import { useBrandOptional } from '@/app/providers/BrandProvider';
import { SITE_NAME, SITE_URL } from '@/config/site';

const TITLE_SEPARATOR = ' | ';

export interface PageMetaOptions {
    /** Replaces the meta description while the page is mounted. */
    description?: string;
    /** Path for the canonical link; defaults to the current pathname. */
    canonicalPath?: string;
}

function upsertMeta(name: string, content: string): void {
    let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
    }
    tag.content = content;
}

function upsertCanonical(href: string): void {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
    }
    link.href = href;
}

function removeCanonical(): void {
    document.head.querySelector('link[rel="canonical"]')?.remove();
}

export function usePageTitle(title?: string, { description, canonicalPath }: PageMetaOptions = {}): void {
    const brandContext = useBrandOptional();
    const brandLoaded = brandContext && !brandContext.loading && !brandContext.isFallback;
    const brandName = brandLoaded ? brandContext.brand.name : '';
    const siteName = brandName || SITE_NAME;

    useEffect(() => {
        document.title = title ? `${title}${TITLE_SEPARATOR}${siteName}` : siteName;
        if (description) upsertMeta('description', description);
        upsertCanonical(`${SITE_URL}${canonicalPath ?? window.location.pathname}`);
        return () => {
            document.title = siteName;
            removeCanonical();
        };
    }, [title, siteName, description, canonicalPath]);
}
